import {
  SemanticColors,
  createThemeColorGetter,
  getColor,
  type THueVariationString
} from 'volta/utils/style-utils';

export const ChartColorScaleVars: THueVariationString[] = [
  'teal',
  'green',
  'yellow',
  'red',
  'orange',
  'purple',
  'blue',
  'green-6',
  'indigo'
];

export const ChartColorScale = [
  getColor('teal-4'),
  getColor('green-4'),
  getColor('yellow-4'),
  getColor('red-4'),
  getColor('orange-4'),
  getColor('purple-4'),
  getColor('blue-4'),
  getColor('green-6'),
  getColor('indigo-4')
];

const inactive = createThemeColorGetter(getColor('gray-7', 0.2), getColor('gray-3', 0.2));

export const volta = (theme: 'light' | 'dark' = 'light') => {
  return {
    color: ChartColorScale,
    backgroundColor: 'rgba(0, 0, 0, 0)',
    textStyle: {
      fontFamily: 'Inter',
      color: SemanticColors.bvText(theme)
    },
    grid: {
      left: 10,
      right: 10,
      containLabel: true
    },
    title: {
      left: 0,
      textStyle: {
        color: SemanticColors.bvText,
        fontFamily: 'Inter',
        fontSize: 16,
        fontWeight: 600
      },
      subtextStyle: {
        color: SemanticColors.bvTextSubdued
      }
    },
    line: {
      itemStyle: {
        borderWidth: 1
      },
      lineStyle: {
        width: 2
      },
      symbolSize: 4,
      symbol: 'emptyCircle',
      smooth: false
    },
    radar: {
      itemStyle: {
        borderWidth: 1
      },
      lineStyle: {
        width: 2
      },
      symbolSize: 4,
      symbol: 'emptyCircle',
      smooth: false
    },
    bar: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      }
    },
    pie: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      }
    },
    scatter: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      }
    },
    boxplot: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      }
    },
    parallel: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      }
    },
    sankey: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      }
    },
    funnel: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      }
    },
    gauge: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      }
    },
    candlestick: {
      itemStyle: {
        color: getColor('red-4'),
        color0: getColor('green-4'),
        borderColor: getColor('red-4'),
        borderColor0: getColor('green-4'),
        borderWidth: 1
      }
    },
    graph: {
      itemStyle: {
        borderWidth: 0,
        borderColor: SemanticColors.bvBorder(theme)
      },
      lineStyle: {
        width: 1,
        color: SemanticColors.bvBorder(theme)
      },
      symbolSize: 4,
      symbol: 'emptyCircle',
      smooth: false,
      color: ChartColorScale,
      label: {
        color: SemanticColors.bvTextSubdued
      }
    },
    categoryAxis: {
      axisLine: {
        show: true,
        lineStyle: {
          color: SemanticColors.bvBorder(theme)
        }
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: SemanticColors.bvBorder(theme)
        }
      },
      axisLabel: {
        show: true,
        color: SemanticColors.bvTextSubdued(theme)
      },
      splitLine: {
        show: false,
        lineStyle: {
          color: [SemanticColors.bvBorderLight(theme)]
        }
      },
      splitArea: {
        show: false,
        areaStyle: {
          color: [getColor('gray-1', 0.2), getColor('teal-1', 0.2)]
        }
      }
    },
    valueAxis: {
      axisLine: {
        show: false,
        lineStyle: {
          color: SemanticColors.bvBorder(theme)
        }
      },
      axisTick: {
        show: false,
        lineStyle: {
          color: SemanticColors.bvBorder(theme)
        }
      },
      axisLabel: {
        show: true,
        color: SemanticColors.bvTextSubdued(theme)
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: [SemanticColors.bvBorderLight(theme)]
        }
      },
      splitArea: {
        show: false,
        areaStyle: {
          color: [getColor('gray-1', 0.2), getColor('teal-1', 0.2)]
        }
      }
    },
    logAxis: {
      axisLine: {
        show: false,
        lineStyle: {
          color: SemanticColors.bvBorder(theme)
        }
      },
      axisTick: {
        show: false,
        lineStyle: {
          color: SemanticColors.bvBorder(theme)
        }
      },
      axisLabel: {
        show: true,
        color: SemanticColors.bvTextSubdued(theme)
      },
      splitLine: {
        show: true,
        lineStyle: {
          color: [SemanticColors.bvBorderLight(theme)]
        }
      },
      splitArea: {
        show: false,
        areaStyle: {
          color: [getColor('gray-1', 0.2), getColor('teal-1', 0.2)]
        }
      }
    },
    timeAxis: {
      axisLine: {
        show: true,
        lineStyle: {
          color: SemanticColors.bvBorder(theme)
        }
      },
      axisTick: {
        show: true,
        lineStyle: {
          color: SemanticColors.bvBorder(theme)
        }
      },
      axisLabel: {
        show: true,
        color: SemanticColors.bvTextSubdued(theme)
      },
      splitLine: {
        show: false,
        lineStyle: {
          color: [SemanticColors.bvBorderLight(theme)]
        }
      },
      splitArea: {
        show: false,
        areaStyle: {
          color: [getColor('gray-1', 0.2), getColor('teal-1', 0.2)]
        }
      }
    },
    toolbox: {
      iconStyle: {
        borderColor: SemanticColors.bvTextSubdued(theme)
      },
      emphasis: {
        iconStyle: {
          borderColor: SemanticColors.bvText(theme)
        }
      }
    },
    legend: {
      left: 0,
      top: 0,
      selectedMode: 'multiple',
      inactiveBorderColor: inactive(theme),
      inactiveColor: inactive(theme),
      textStyle: {
        color: SemanticColors.bvText(theme)
      }
    },
    tooltip: {
      textStyle: {
        fontFamily: 'sans-serif'
      },
      axisPointer: {
        lineStyle: {
          color: SemanticColors.bvBorder(theme),
          width: 1
        },
        crossStyle: {
          color: SemanticColors.bvBorder(theme),
          width: 1
        }
      }
    },

    visualMap: {
      color: [getColor('red-6'), getColor('orange-4'), getColor('yellow-3')]
    },
    dataZoom: {
      height: 25,
      handleSize: '100%',
      textStyle: {},
      fillerColor: getColor('teal-4', 0.1),
      borderColor: SemanticColors.bvBorder(theme),
      dataBackground: {
        lineStyle: {
          color: getColor('teal-4')
        },
        areaStyle: {
          color: getColor('teal-1')
        }
      }
    },
    markPoint: {
      label: {
        color: '#eee'
      },
      emphasis: {
        label: {
          color: '#eee'
        }
      }
    },
    markLine: {
      label: {
        color: SemanticColors.bvText(theme),
        textBorderWidth: 0,
        textBorderColor: 'transparent'
      },
      animation: false
    }
  };
};
